/**
 * @fileoverview Entidad AnalyticsReport - Representa un reporte analítico
 * Implementa patrón Aggregate Root con inmutabilidad garantizada
 */

export const VALID_REPORT_TYPES = [
  'sales_trend', 
  'inventory_status', 
  'customer_metrics', 
  'conversion_funnel', 
  'product_performance',
  'anomaly_detection'
];

export const VALID_TIME_RANGES = [
  'today', 
  'yesterday', 
  'last_7_days', 
  'last_30_days', 
  'this_month', 
  'last_month', 
  'this_year', 
  'custom'
];

/**
 * @class AnalyticsReport
 * @description Entidad principal que representa un reporte analítico
 * Implementación inmutable siguiendo el patrón Value Object
 */
export default class AnalyticsReport {
  /**
   * @constructor
   * @param {Object} props - Propiedades del reporte
   * @param {string} props.id - Identificador único del reporte
   * @param {string} props.type - Tipo de reporte (ventas, inventario, etc)
   * @param {string} props.title - Título descriptivo del reporte
   * @param {string} props.timeRange - Rango temporal del reporte
   * @param {Date} props.startDate - Fecha inicial para rangos personalizados
   * @param {Date} props.endDate - Fecha final para rangos personalizados
   * @param {Array} props.metrics - Métricas calculadas para el reporte
   * @param {Array} props.dimensions - Dimensiones para segmentación
   * @param {Object} props.filters - Filtros aplicados al reporte
   * @param {Object} props.metadata - Metadatos adicionales
   */
  constructor({
    id,
    type,
    title,
    timeRange = 'last_30_days',
    startDate = null,
    endDate = null,
    metrics = [],
    dimensions = [],
    filters = {},
    metadata = {}
  }) {
    this._validateProps({id, type, title, timeRange});
    
    this._id = id;
    this._type = type;
    this._title = title;
    this._timeRange = timeRange;
    this._startDate = startDate ? new Date(startDate) : null;
    this._endDate = endDate ? new Date(endDate) : null;
    this._metrics = Object.freeze([...metrics]);
    this._dimensions = Object.freeze([...dimensions]);
    this._filters = Object.freeze({...filters});
    this._metadata = Object.freeze({...metadata});
    this._createdAt = new Date();
    
    // Congelar la instancia para garantizar inmutabilidad
    Object.freeze(this);
  }
  
  /**
   * Validación de propiedades según reglas de negocio
   * @private
   * @param {Object} props - Propiedades a validar
   * @throws {Error} Si alguna validación falla
   */
  _validateProps({id, type, title, timeRange}) {
    if (!id || typeof id !== 'string') {
      throw new Error('Report must have a valid id string');
    }
    
    if (!VALID_REPORT_TYPES.includes(type)) {
      throw new Error(`Report type must be one of: ${VALID_REPORT_TYPES.join(', ')}`);
    }
    
    if (!title || typeof title !== 'string') {
      throw new Error('Report must have a valid title');
    }
    
    if (timeRange && !VALID_TIME_RANGES.includes(timeRange)) {
      throw new Error(`Time range must be one of: ${VALID_TIME_RANGES.join(', ')}`);
    }
    
    if (timeRange === 'custom' && (!this._startDate || !this._endDate)) {
      throw new Error('Custom time range requires both startDate and endDate');
    }
  }
  
  // Getters - No hay setters para garantizar inmutabilidad
  get id() { return this._id; }
  get type() { return this._type; }
  get title() { return this._title; }
  get timeRange() { return this._timeRange; }
  get startDate() { return this._startDate ? new Date(this._startDate) : null; }
  get endDate() { return this._endDate ? new Date(this._endDate) : null; }
  get metrics() { return [...this._metrics]; }
  get dimensions() { return [...this._dimensions]; }
  get filters() { return {...this._filters}; }
  get metadata() { return {...this._metadata}; }
  get createdAt() { return new Date(this._createdAt); }
  
  /**
   * Crea una nueva instancia con propiedades actualizadas (patrón inmutable)
   * @param {Object} props - Propiedades que se desean actualizar
   * @returns {AnalyticsReport} Nueva instancia con propiedades actualizadas
   */
  with(props) {
    return new AnalyticsReport({
      id: this._id,
      type: this._type,
      title: this._title,
      timeRange: this._timeRange,
      startDate: this._startDate,
      endDate: this._endDate,
      metrics: this._metrics,
      dimensions: this._dimensions,
      filters: this._filters,
      metadata: this._metadata,
      ...props
    });
  }
  
  /**
   * Aplica un nuevo filtro al reporte
   * @param {string} name - Nombre del filtro
   * @param {any} value - Valor del filtro
   * @returns {AnalyticsReport} Nuevo reporte con el filtro aplicado
   */
  applyFilter(name, value) {
    const newFilters = {
      ...this._filters,
      [name]: value
    };
    
    return this.with({ filters: newFilters });
  }
  
  /**
   * Cambia el rango de tiempo del reporte
   * @param {string} timeRange - Nuevo rango de tiempo
   * @param {Date} [startDate] - Fecha inicio para rangos personalizados
   * @param {Date} [endDate] - Fecha fin para rangos personalizados
   * @returns {AnalyticsReport} Nuevo reporte con el rango de tiempo actualizado
   */
  changeTimeRange(timeRange, startDate = null, endDate = null) {
    if (!VALID_TIME_RANGES.includes(timeRange)) {
      throw new Error(`Time range must be one of: ${VALID_TIME_RANGES.join(', ')}`);
    }
    
    if (timeRange === 'custom' && (!startDate || !endDate)) {
      throw new Error('Custom time range requires both startDate and endDate');
    }
    
    return this.with({
      timeRange,
      startDate: timeRange === 'custom' ? startDate : null,
      endDate: timeRange === 'custom' ? endDate : null
    });
  }
  
  /**
   * Convierte la entidad a un objeto plano para serialización
   * @returns {Object} Representación plana del reporte
   */
  toJSON() {
    return {
      id: this._id,
      type: this._type,
      title: this._title,
      timeRange: this._timeRange,
      startDate: this._startDate ? this._startDate.toISOString() : null,
      endDate: this._endDate ? this._endDate.toISOString() : null,
      metrics: this._metrics,
      dimensions: this._dimensions,
      filters: this._filters,
      metadata: this._metadata,
      createdAt: this._createdAt.toISOString()
    };
  }
}

// Exportado con ES Modules (export default) al inicio de la clase
